import React, { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { CircleButton, YatFadeIn, YatSidebar } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { useLockBodyScroll } from '@app/helpers';
import { setMobileSidebarIsOpen } from '@app/store/actions/dashboard';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import './MobileSidebar.scss';

export interface YatDashboardMobileSidebarProps {
    isPrimaryYatPicker?: boolean;
}

export function YatDashboardMobileSidebar({ isPrimaryYatPicker = false }: YatDashboardMobileSidebarProps) {
    const [translate] = useTranslation(['dashboard', 'groups']);

    const dispatch = useAppDispatch();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const { mobileSidebarIsOpen } = useAppSelector((state) => state.dashboard);

    const { currentEmoji } = useEmojisStore();

    useLockBodyScroll(mobileSidebarIsOpen, 'visible');

    const closeSidebar = useCallback(() => {
        dispatch(setMobileSidebarIsOpen(false));
    }, [dispatch]);

    useEffect(() => {
        closeSidebar();
    }, [currentEmoji, primaryYat, closeSidebar]);

    return (
        <YatFadeIn appear={mobileSidebarIsOpen}>
            <div
                id="yat-dashboard-mobile-sidebar"
                className="yat-dashboard-mobile-sidebar"
                aria-hidden={mobileSidebarIsOpen ? 'false' : 'true'}
            >
                <div className="YatPickerLabelWrapper">
                    <p className="YatPickerLabel">{translate('groups:select-your-yat')}</p>
                </div>

                <div className="yat-dashboard-mobile-sidebar__inner">
                    <YatSidebar isPrimaryYatPicker={isPrimaryYatPicker} expanded={true} />
                </div>

                <CircleButton
                    classes="yat-dashboard-mobile-sidebar__close"
                    size="large"
                    ariaLabel={translate('close_your_yats_list')}
                    onClick={closeSidebar}
                >
                    <CloseIcon aria-hidden="true" />
                </CircleButton>
            </div>
        </YatFadeIn>
    );
}
